/**
 * Vínculo entre contatos de canais diferentes do MESMO lead, mostrado no cartão
 * do contato dos 4 inboxes ("Também é contato em:").
 *
 * O backend grava `linked_contacts` quando o agente cadastra no WhatsApp um lead
 * que chegou pelo Instagram ou pelo Messenger (services/cross_channel_lead.py).
 * GET /api/contacts/{chave}/linked-contacts devolve cada vínculo com `channels`
 * (inboxes onde o outro contato existe), `name`, `username` e `phone`.
 *
 * Canal, rótulo, rota e deep-link vêm do mapa de capacidades
 * (utils/channels.js). O deep-link é o `state.openChat` que os 4 Inbox já leem
 * (notificação, agenda, painel).
 */
import { formatPhoneNumber } from '../../utils/wa-personal/chat_utils';
import {
  channelOf, isOpaqueKey, isPhoneChannel, keyValueOf, labelOf, openChatState, routeOf, supports,
} from '../../utils/channels';

const somenteDigitos = (valor) => String(valor || '').replace(/\D/g, '');

// Nome que é só número (IGSID, telefone) não identifica ninguém na tela.
const nomeLegivel = (valor) => {
  const texto = String(valor || '').trim();
  return texto && !/^[\d\s()+-]+$/.test(texto) ? texto : '';
};

// Canal pelo PREFIXO da chave: chave opaca nunca é lida como telefone. Chave
// sem prefixo é 'whatsapp' (os dois inboxes de WhatsApp compartilham o número).
const canalDaChave = (link) => {
  const key = String((link && link.key) || '').trim();
  return isOpaqueKey(key) ? channelOf(key) : 'whatsapp';
};

const usuarioDoLink = (link) => String((link && link.username) || '').trim().replace(/^@/, '');

/** Canal do vinculo pelo PREFIXO da chave: 'instagram' | 'messenger' | 'whatsapp'. */
export const linkedContactChannel = (link) => canalDaChave(link);

/** Rotulo curto para chip: "WhatsApp", "Instagram @fulano", "Messenger". */
export const linkedContactChipLabel = (link) => {
  const canal = canalDaChave(link);
  if (canal === 'whatsapp') return 'WhatsApp';
  const rotulo = labelOf(canal, 'short');
  const usuario = supports(canal, 'username') ? usuarioDoLink(link) : '';
  return usuario ? `${rotulo} @${usuario}` : rotulo;
};

export const linkedContactLabel = (link) => {
  const canal = canalDaChave(link);
  if (canal !== 'whatsapp') {
    const rotulo = labelOf(canal, 'short');
    const usuario = supports(canal, 'username') ? usuarioDoLink(link) : '';
    if (usuario) return `${rotulo} @${usuario}`;
    const nome = nomeLegivel(link && link.name);
    return nome ? `${rotulo} ${nome}` : rotulo;
  }
  const digitos = somenteDigitos((link && link.phone) || (link && link.key));
  const numero = String(formatPhoneNumber(digitos) || '').replace(/^\+55 /, '');
  return numero ? `WhatsApp ${numero}` : 'WhatsApp';
};

/** { route, state } para abrir a conversa do outro canal, ou null quando o
 *  outro contato não foi achado em nenhum inbox (fica só o texto). */
export const linkedContactTarget = (link, defaultChannel) => {
  const canais = Array.isArray(link && link.channels) ? link.channels : [];
  const canal = canalDaChave(link);
  if (canal !== 'whatsapp') {
    const valor = keyValueOf(link && link.key);
    return valor && canais.includes(canal)
      ? { route: routeOf(canal), state: { openChat: openChatState(canal, valor) } }
      : null;
  }
  const phone = somenteDigitos((link && link.phone) || (link && link.key));
  const inbox = [defaultChannel, 'wa_personal', 'wa_business']
    .find((c) => isPhoneChannel(c) && canais.includes(c));
  return phone && inbox
    ? { route: routeOf(inbox), state: { openChat: openChatState(inbox, phone) } }
    : null;
};
